/**
 * ActionPlanListPage — Internal monitoring dashboard for Action Plans
 *
 * Shows:
 *   1. Aggregate outstanding AP counts by site/scope (from monitoring summary)
 *   2. Full AP list with status, scope, result value, open item counts
 *   3. Carry-forward candidate navigator
 *   4. [Admin only] Mark as Dummy + Purge Dummy APs controls
 *
 * Route: /action-plans  (ProtectedRoute, internal users only)
 */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  AlertCircle, ArrowRight, BarChart2,
  ChevronDown, ChevronUp, Loader2, Trash2, Flag,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { actionPlansService, dummyApService } from '../services';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { getApiError } from '@/lib/utils';

const STATUS_COLORS = {
  open:               'bg-amber-100 text-amber-800 border-amber-200',
  partially_resolved: 'bg-blue-100 text-blue-800 border-blue-200',
  resolved:           'bg-green-100 text-green-800 border-green-200',
};

function StatusBadge({ status }) {
  const cls = STATUS_COLORS[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  const label = {
    open: 'Open',
    partially_resolved: 'Partially Resolved',
    resolved: 'Resolved',
  }[status] || status;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${cls}`}>
      {label}
    </span>
  );
}

export default function ActionPlanListPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { toast } = useToast();

  const [plans, setPlans]               = useState([]);
  const [monitoring, setMonitoring]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [filterScope, setFilterScope]   = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showMonitoring, setShowMonitoring] = useState(true);

  // Dummy AP controls (admin only)
  const [showDummy, setShowDummy]           = useState(false);
  const [markDummyTarget, setMarkDummyTarget] = useState(null);   // { id, site_name, scope }
  const [markReason, setMarkReason]         = useState('');
  const [markingDummy, setMarkingDummy]     = useState(false);
  const [showPurgeDialog, setShowPurgeDialog] = useState(false);
  const [selectedForPurge, setSelectedForPurge] = useState(new Set());
  const [purgeReason, setPurgeReason]       = useState('');
  const [purgingDummy, setPurgingDummy]     = useState(false);

  const loadPlans = (includeDummy = false) => {
    setLoading(true);
    setError(null);
    Promise.all([
      actionPlansService.list(includeDummy ? { show_dummy: true } : {}),
      actionPlansService.monitoringSummary({}),
    ])
      .then(([planList, monData]) => { setPlans(planList); setMonitoring(monData); })
      .catch(e => setError(getApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadPlans(showDummy); }, [showDummy]);

  const filteredPlans = plans.filter(p => {
    if (filterScope && p.scope !== filterScope) return false;
    if (filterStatus && p.status !== filterStatus) return false;
    return true;
  });

  const dummyPlans = plans.filter(p => p.is_dummy);
  const totalOpen  = plans.filter(p => p.status === 'open' && !p.is_dummy).length;
  const totalItems = plans.filter(p => !p.is_dummy).reduce((s, p) => s + (p.open_item_count || 0), 0);
  const cfCount    = monitoring.reduce((s, m) => s + (m.carry_forward_count || 0), 0);

  // ── Mark as Dummy ──────────────────────────────────────────────────────────
  const handleMarkDummy = async () => {
    if (!markDummyTarget || markReason.trim().length < 10) return;
    setMarkingDummy(true);
    try {
      await dummyApService.markDummy(markDummyTarget.id, markReason.trim());
      toast({ title: 'Marked as Dummy', description: `AP #${markDummyTarget.id} is now excluded from operational views.` });
      setMarkDummyTarget(null);
      setMarkReason('');
      loadPlans(showDummy);
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to mark as dummy'), variant: 'destructive' });
    } finally {
      setMarkingDummy(false);
    }
  };

  // ── Purge Dummy APs ────────────────────────────────────────────────────────
  const handlePurgeDummy = async () => {
    if (selectedForPurge.size === 0 || purgeReason.trim().length < 10) return;
    setPurgingDummy(true);
    try {
      const result = await dummyApService.purgeDummy([...selectedForPurge], purgeReason.trim());
      toast({
        title: 'Purge Complete',
        description: result.message,
      });
      setShowPurgeDialog(false);
      setSelectedForPurge(new Set());
      setPurgeReason('');
      loadPlans(showDummy);
    } catch (err) {
      toast({ title: 'Purge Failed', description: getApiError(err, 'Purge failed'), variant: 'destructive' });
    } finally {
      setPurgingDummy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6" data-testid="ap-list-page">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Action Plans</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {totalOpen} open &nbsp;·&nbsp; {totalItems} unresolved findings
            {cfCount > 0 && <span className="text-amber-600 ml-2">&nbsp;{cfCount} carried forward</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <>
              <button
                onClick={() => setShowDummy(v => !v)}
                className={`text-xs px-3 py-1.5 rounded border transition-colors ${showDummy ? 'bg-orange-100 text-orange-700 border-orange-300' : 'bg-gray-100 text-gray-500 border-gray-300 hover:bg-gray-200'}`}
                data-testid="toggle-show-dummy"
              >
                <Flag className="inline w-3 h-3 mr-1" />
                {showDummy ? `Hide Dummy (${dummyPlans.length})` : `Show Dummy (${dummyPlans.length})`}
              </button>
              {dummyPlans.length > 0 && showDummy && (
                <button
                  onClick={() => setShowPurgeDialog(true)}
                  className="text-xs px-3 py-1.5 rounded border border-red-300 text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
                  data-testid="open-purge-dialog-btn"
                >
                  <Trash2 className="inline w-3 h-3 mr-1" />
                  Purge Dummy APs
                </button>
              )}
            </>
          )}
          <Link
            to="/action-plans/report"
            className="text-sm text-blue-600 hover:underline flex items-center gap-1"
            data-testid="ap-report-nav-link"
          >
            <BarChart2 size={14} /> CA Report
          </Link>
        </div>
      </div>

      {/* Monitoring Summary */}
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        <button
          className="w-full flex items-center justify-between px-5 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          onClick={() => setShowMonitoring(v => !v)}
          data-testid="toggle-monitoring-btn"
        >
          <div className="flex items-center gap-2">
            <BarChart2 className="w-4 h-4 text-blue-500" />
            Outstanding by Site / Scope
          </div>
          {showMonitoring ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {showMonitoring && (
          <div className="divide-y">
            {monitoring.length === 0 && (
              <p className="px-5 py-4 text-sm text-gray-400">No outstanding action plans.</p>
            )}
            {monitoring.map((m, i) => (
              <div key={i} className="flex items-center px-5 py-2.5 gap-4 hover:bg-gray-50 text-sm">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-800 truncate">{m.site_name || `Site ${m.site_id}`}</p>
                  {m.client_name && <p className="text-xs text-gray-400">{m.client_name}</p>}
                </div>
                <span className="text-xs text-gray-500 font-medium">{m.scope}</span>
                <span className="text-xs bg-amber-100 text-amber-800 rounded px-2 py-0.5">{m.open_item_count} item{m.open_item_count !== 1 ? 's' : ''}</span>
                {m.carry_forward_count > 0 && (
                  <span className="text-xs bg-orange-100 text-orange-800 rounded px-2 py-0.5">{m.carry_forward_count} CF</span>
                )}
                {m.oldest_open_due_date && (
                  <span className="text-xs text-gray-400">Due: {new Date(m.oldest_open_due_date).toLocaleDateString('en-GB')}</span>
                )}
                <span className="text-xs text-gray-400">{m.open_plan_count} plan{m.open_plan_count !== 1 ? 's' : ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <select
          className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          value={filterScope}
          onChange={e => setFilterScope(e.target.value)}
          data-testid="filter-scope"
        >
          <option value="">All Scopes</option>
          <option value="FOOD">Food</option>
          <option value="BEVERAGE">Beverage</option>
        </select>
        <select
          className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          data-testid="filter-status"
        >
          <option value="">All Statuses</option>
          <option value="open">Open</option>
          <option value="partially_resolved">Partially Resolved</option>
          <option value="resolved">Resolved</option>
        </select>
        {(filterScope || filterStatus) && (
          <button
            className="text-xs text-blue-600 hover:text-blue-800"
            onClick={() => { setFilterScope(''); setFilterStatus(''); }}
          >Clear</button>
        )}
        <p className="text-xs text-gray-400 ml-auto">{filteredPlans.length} plan{filteredPlans.length !== 1 ? 's' : ''}</p>
      </div>

      {/* Plans Table */}
      <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
        {filteredPlans.length === 0 ? (
          <p className="px-5 py-8 text-sm text-gray-400 text-center">No action plans found.</p>
        ) : (
          <div className="divide-y">
            {filteredPlans.map(p => (
              <div
                key={p.id}
                className={`flex items-center px-5 py-3 gap-3 hover:bg-gray-50 ${p.is_dummy ? 'bg-orange-50 opacity-75' : 'cursor-pointer'}`}
                onClick={() => !p.is_dummy && navigate(`/action-plans/${p.id}`)}
                data-testid={`ap-row-${p.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-gray-800 text-sm">{p.site_name || `Site ${p.site_id}`}</p>
                    {p.client_name && <span className="text-xs text-gray-400">{p.client_name}</span>}
                    {p.is_dummy && (
                      <span className="text-xs bg-orange-200 text-orange-800 border border-orange-300 rounded px-1.5 py-0.5 font-semibold" data-testid={`dummy-badge-${p.id}`}>
                        DUMMY
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                    <span>{p.scope}</span>
                    {p.stock_number && <span>· {p.stock_number}</span>}
                    {p.audit_date && <span>· {new Date(p.audit_date).toLocaleDateString('en-GB')}</span>}
                    {p.is_dummy && p.dummy_reason && (
                      <span className="text-orange-600 italic">Reason: {p.dummy_reason}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {!p.is_dummy && p.source_result_value != null && (
                    <span className={`text-sm font-medium ${p.source_result_value < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {p.source_result_value > 0 ? '+' : ''}{p.source_result_value}%
                    </span>
                  )}
                  {!p.is_dummy && p.open_item_count > 0 && (
                    <span className="text-xs bg-amber-100 text-amber-800 rounded px-2 py-0.5">
                      {p.open_item_count} open
                    </span>
                  )}
                  {!p.is_dummy && <StatusBadge status={p.status} />}
                  {isAdmin && !p.is_dummy && (
                    <button
                      onClick={e => { e.stopPropagation(); setMarkDummyTarget({ id: p.id, site_name: p.site_name, scope: p.scope }); setMarkReason(''); }}
                      className="text-gray-300 hover:text-orange-500 transition-colors"
                      title="Mark as Dummy"
                      data-testid={`mark-dummy-btn-${p.id}`}
                    >
                      <Flag className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {isAdmin && p.is_dummy && showDummy && (
                    <input
                      type="checkbox"
                      checked={selectedForPurge.has(p.id)}
                      onChange={e => {
                        e.stopPropagation();
                        setSelectedForPurge(prev => {
                          const next = new Set(prev);
                          e.target.checked ? next.add(p.id) : next.delete(p.id);
                          return next;
                        });
                      }}
                      className="accent-red-500"
                      title="Select for purge"
                      data-testid={`purge-select-${p.id}`}
                    />
                  )}
                  {!p.is_dummy && <ArrowRight className="w-4 h-4 text-gray-300" />}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Mark as Dummy Dialog ──────────────────────────────────────────── */}
      {markDummyTarget && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4" data-testid="mark-dummy-dialog">
            <h2 className="text-base font-semibold text-gray-900">Mark as Dummy AP</h2>
            <p className="text-sm text-gray-600">
              <strong>{markDummyTarget.site_name}</strong> — {markDummyTarget.scope} (AP #{markDummyTarget.id})
            </p>
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
              This AP will be immediately excluded from all operational views, dashboards, analytics and carry-forward.
              It is <strong>not deleted</strong> — use Purge Dummy APs to hard-delete after review.
              This action is recorded in the security audit log.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason <span className="text-red-500">*</span> <span className="text-gray-400 font-normal">(min 10 chars)</span>
              </label>
              <textarea
                value={markReason}
                onChange={e => setMarkReason(e.target.value)}
                rows={3}
                placeholder="e.g. Created during system testing on 2026-10-15; not a real audit event."
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400"
                data-testid="mark-dummy-reason-input"
              />
              <p className="text-xs text-gray-400 mt-1">{markReason.trim().length}/10 minimum</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setMarkDummyTarget(null)} disabled={markingDummy}>
                Cancel
              </Button>
              <Button
                size="sm"
                className="bg-orange-600 hover:bg-orange-700 text-white"
                onClick={handleMarkDummy}
                disabled={markingDummy || markReason.trim().length < 10}
                data-testid="confirm-mark-dummy-btn"
              >
                {markingDummy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Mark as Dummy'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Purge Dummy APs Dialog ────────────────────────────────────────── */}
      {showPurgeDialog && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4" data-testid="purge-dummy-dialog">
            <h2 className="text-base font-semibold text-red-700 flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> Purge Dummy APs
            </h2>
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-3">
              <strong>Irreversible.</strong> Selected APs and their items, corrective actions, notification logs and
              carry-forward links will be permanently deleted. The underlying summary audit reports and genuine
              carry-forward items in live plans are protected. A restricted security record is retained.
            </p>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                Selected for purge: <span className="font-bold text-red-600">{selectedForPurge.size}</span> AP{selectedForPurge.size !== 1 ? 's' : ''}
              </p>
              {selectedForPurge.size === 0 && (
                <p className="text-xs text-amber-600">No APs selected — tick the checkboxes in the list above.</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Purge reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={purgeReason}
                onChange={e => setPurgeReason(e.target.value)}
                rows={3}
                placeholder="e.g. Removing test APs created during UAT October 2026."
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-400"
                data-testid="purge-reason-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowPurgeDialog(false)} disabled={purgingDummy}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={handlePurgeDummy}
                disabled={purgingDummy || selectedForPurge.size === 0 || purgeReason.trim().length < 10}
                data-testid="confirm-purge-btn"
              >
                {purgingDummy
                  ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />Purging…</>
                  : `Permanently Purge ${selectedForPurge.size} AP${selectedForPurge.size !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
